import { BASE_URL } from "src/constants/vars";
import { setCredentials, logOut } from "../reducers/auth";
import { fetchBaseQuery, createApi, FetchArgs } from '@reduxjs/toolkit/query/react';
import { clearAuthStorage } from "src/utils/auth";
import i18n from "src/setup/i18n";
import { MALEK_STORE_TOKEN_KEY } from "src/constants/storageKeys";

const getRequestUrl = (args: string | FetchArgs) => {
    return typeof args === "string" ? args : args.url;
}

export const shouldSkipReauth = (args: string | FetchArgs) => {
    const url = getRequestUrl(args);
    return [
        "/auth/login/",
        "/auth/refresh/",
        "/accounts/register/",
        "/accounts/activation/",
        "/accounts/resend/activation/",
        "/accounts/password/recovery/",
        "/accounts/password/confirmation/",
        "/accounts/password/reset/",
    ].some((path) => url.includes(path));
}

const baseQuery = fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }: { getState: any }) => {
        const accessToken = getState().auth.access;
        if (accessToken) {
            headers.set("Authorization", `Bearer ${accessToken}`);
        }
        headers.set('Accept-Language', i18n.language);
        return headers
    }
})

let refreshPromise: Promise<{ access: string; refresh?: string } | null> | null = null;

const logOutAction = (api: any) => {
    api.dispatch(logOut());
    clearAuthStorage();
    api.dispatch(apiSlice.util.resetApiState());
}

const baseQueryWithReauth: typeof baseQuery = async (args, api: any, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401 && !shouldSkipReauth(args)) {
        // 1️⃣ Get refresh token from Redux state
        const stateRefreshToken = api.getState().auth?.refresh;

        // 2️⃣ Get refresh token from local storage
        const localStorageRefreshToken = localStorage.getItem(MALEK_STORE_TOKEN_KEY);

        // 3️⃣ Use whichever exists
        const refreshToken = stateRefreshToken || localStorageRefreshToken;

        if (refreshToken) {
            try {
                if (!refreshPromise) {
                    refreshPromise = fetch(`${BASE_URL}/auth/refresh/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': i18n.language,
                        },
                        body: JSON.stringify({
                            refresh: refreshToken,
                        }),
                    })
                        .then(async (response) => {
                            if (!response.ok) throw new Error("Refresh token invalid");
                            return response.json();
                        })
                        .finally(() => {
                            refreshPromise = null;
                        });
                }

                const refreshedTokens = await refreshPromise;
                if (!refreshedTokens?.access) throw new Error("Refresh token invalid");
                const { access, refresh } = refreshedTokens;

                // Save the new tokens to Redux
                api.dispatch(setCredentials({
                    ...api.getState().auth,
                    access,
                    refresh: refresh || refreshToken, // keep existing refresh if backend doesn't return it
                }));

                // Optionally update localStorage
                localStorage.setItem(MALEK_STORE_TOKEN_KEY, refresh || refreshToken);

                // Retry the original query
                result = await baseQuery(args, api, extraOptions);
            }
            catch (error) {
                logOutAction(api);
            }
        }
        else {
            logOutAction(api);
        }
    }

    return result;
};



const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    tagTypes: ["Wishlist", "Product", "Profile", "Cart"],
    endpoints: (builder) => ({

    })
})

export default apiSlice;
